/**
 * Time Tracking — types + pure helpers for the worked-hours vs target balance.
 *
 * Model (Phase 1):
 *   - Daily target = weeklyWorkingHours / 5 on Mon–Fri; 0 on weekends, public
 *     holidays (the user's holiday calendar), and full-day approved absences;
 *     halved for half-day absences.
 *   - Worked minutes per day come from a check-in/break/check-out session, or a
 *     manual override (missed/adjusted days).
 *   - Hour status (YTD) = openingBalance + Σ(worked − target) from startDate.
 *     (Overtime compensation is subtracted in a later phase.)
 *
 * All durations are MINUTES. Dates are wall-clock YYYY-MM-DD strings.
 */
import { isHalfHoliday, isOvertimeCompensationType } from './absence.js';
import { dayHoursForDate, resolveWorkTimeEntry, } from './employment.js';
/** How a day's totals were produced — shown as a tag to the user + manager. */
export const TIME_ENTRY_SOURCES = ['tracked', 'updated', 'manual'];
/** Minimum recommended/expected total break in a work-day (minutes). Saving a
 *  manual day below this prompts the user to confirm (30+ is often legally
 *  required, location-dependent). */
export const MIN_RECOMMENDED_BREAK_MIN = 30;
/** Auto clock-out: if a session is still open past this LOCAL time (minutes from
 *  local midnight), the forgotten clock-out is stopped automatically… */
export const AUTO_CLOCKOUT_CUTOFF_MIN = 19 * 60; // 19:00 local
/** …and the session's end is RECORDED at this local time. */
export const AUTO_CLOCKOUT_END_MIN = 17 * 60 + 30; // 17:30 local
/**
 * Timezone-config-free auto-clock-out math. Given the user's LOCAL date
 * (YYYY-MM-DD) and the UTC offset their browser reported at clock time
 * (`getTimezoneOffset()` minutes, positive = behind UTC), return the absolute
 * UTC instants (ms) for that day's local 19:00 cutoff and local 17:30 end.
 *
 * local wall-clock T → UTC = (localMidnight-as-UTC) + T + offset  (all ms).
 */
export function autoClockOutInstants(dateStr, tzOffsetMinutes) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!m)
        return { cutoffMs: NaN, endMs: NaN };
    const localMidnight = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const toUtc = (minsOfDay) => localMidnight + (minsOfDay + tzOffsetMinutes) * 60_000;
    return {
        cutoffMs: toUtc(AUTO_CLOCKOUT_CUTOFF_MIN),
        endMs: toUtc(AUTO_CLOCKOUT_END_MIN),
    };
}
/** Normalize an entry's sessions, synthesizing one from legacy fields. */
export function entrySessions(e) {
    if (e.sessions && e.sessions.length > 0)
        return e.sessions;
    if (e.checkInAt) {
        return [{ checkInAt: e.checkInAt, checkOutAt: e.checkOutAt ?? null, breaks: e.breaks ?? [] }];
    }
    return [];
}
/** Minutes targeted on a normal working weekday (Mon–Fri), from weekly hours. */
export function dailyStandardMinutes(weeklyHours) {
    if (!weeklyHours || weeklyHours <= 0)
        return 0;
    return Math.round((weeklyHours / 5) * 60);
}
/** Day-of-week for a YYYY-MM-DD (0=Sun..6=Sat), computed in UTC to avoid TZ drift. */
export function isoWeekday(dateStr) {
    return new Date(`${dateStr}T00:00:00Z`).getUTCDay();
}
export function isWeekend(dateStr) {
    const d = isoWeekday(dateStr);
    return d === 0 || d === 6;
}
/**
 * Target minutes for a specific day, netting out weekends, holidays and approved
 * absences. `absenceFraction` = fraction of the day on approved leave (0, 0.5, 1).
 */
export function targetMinutesForDay(args) {
    if (args.employmentEndDate && args.date > args.employmentEndDate)
        return 0;
    if (args.isHoliday)
        return 0;
    const std = args.workTime !== undefined && args.workTime !== null
        ? Math.round(dayHoursForDate(args.workTime, args.date) * 60) // 0 on weekend + non-working weekday
        : isWeekend(args.date)
            ? 0
            : dailyStandardMinutes(args.weeklyHours);
    const due = args.isHalfHoliday ? 0.5 : 1;
    const remaining = Math.max(0, due - Math.min(1, args.absenceFraction));
    return Math.round(std * remaining);
}
/** Break ms of one session. Open break counted up to `nowMs` (if given). */
function sessionBreakMs(s, nowMs) {
    let ms = 0;
    for (const b of s.breaks ?? []) {
        if (!b.start)
            continue;
        if (!b.end && nowMs == null)
            continue; // open break only counts live
        const end = b.end ? b.end.toMillis() : nowMs;
        ms += Math.max(0, end - b.start.toMillis());
    }
    return ms;
}
/**
 * Worked minutes across all sessions: Σ(checkout − checkin − breaks). `manual`
 * wins. Open sessions are counted only when `nowMs` is given (live view) —
 * server stores totals for closed sessions, the client adds the live one.
 */
export function computeWorkedMinutes(entry, nowMs) {
    if (entry.manualMinutes != null)
        return Math.max(0, Math.round(entry.manualMinutes));
    let ms = 0;
    for (const s of entrySessions(entry)) {
        if (!s.checkOutAt && nowMs == null)
            continue;
        const out = s.checkOutAt ? s.checkOutAt.toMillis() : nowMs;
        ms += Math.max(0, out - s.checkInAt.toMillis() - sessionBreakMs(s, nowMs));
    }
    return Math.round(ms / 60000);
}
/** Break minutes across all sessions. `manualBreakMinutes` wins. */
export function computeBreakMinutes(entry, nowMs) {
    if (entry.manualBreakMinutes != null)
        return Math.max(0, Math.round(entry.manualBreakMinutes));
    let ms = 0;
    for (const s of entrySessions(entry))
        ms += sessionBreakMs(s, nowMs);
    return Math.round(ms / 60000);
}
export const TIME_ENTRY_ID = (email, date) => `${email.toLowerCase()}__${date}`;
// --- Monthly statements (manager approval) --------------------------------
export const TIME_STATEMENT_STATUSES = ['submitted', 'approved', 'rejected'];
export const TIME_STATEMENT_ID = (email, period) => `${email.toLowerCase()}__${period}`;
/** "2026-06" → "June 2026". */
export function periodLabel(period) {
    const [y, m] = period.split('-').map(Number);
    if (!y || !m)
        return period;
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    });
}
/** First + last YYYY-MM-DD of a period. */
export function periodBounds(period) {
    const [y, m] = period.split('-').map(Number);
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return { first: `${period}-01`, last: `${period}-${String(last).padStart(2, '0')}` };
}
/** Due date (YYYY-MM-DD) = the 5th business day (Mon–Fri) of the month AFTER the
 *  period. Public holidays aren't subtracted (calendars differ per person). */
export function statementDueDate(period) {
    const [y, m] = period.split('-').map(Number);
    const d = new Date(Date.UTC(y, m, 1)); // first of next month (m is 1-based → month index m)
    let business = 0;
    while (business < 5) {
        const dow = d.getUTCDay();
        if (dow !== 0 && dow !== 6)
            business++;
        if (business < 5)
            d.setUTCDate(d.getUTCDate() + 1);
    }
    return d.toISOString().slice(0, 10);
}
export function timeStatementStatusLabel(s) {
    return s === 'submitted' ? 'Submitted' : s === 'approved' ? 'Approved' : 'Rejected';
}
export function minutesToHhMm(min) {
    const sign = min < 0 ? '-' : '';
    const m = Math.abs(Math.round(min));
    return `${sign}${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}
export function minutesToHours(min) {
    return Math.round((min / 60) * 100) / 100;
}
/** Fraction of a day on approved leave (0/0.5/1). Work-from-anywhere is still
 *  working, so it does NOT reduce the target. */
/**
 * What covers `date` for the day table: the approved absence's type (plus the
 * special-leave kind), whether it is a half day, and whether it takes the day
 * off the target (work from anywhere keeps the target, so it is label-only).
 * Null when nothing approved covers the date.
 */
export function absenceLabelForDate(date, absences) {
    let best = null;
    for (const a of absences) {
        if (a.status !== 'approved')
            continue;
        if (date < a.startDate || date > a.endDate)
            continue;
        const wfa = a.type === 'Work from anywhere';
        let f = wfa ? 0 : 1;
        if (!wfa) {
            if (a.startDate === a.endDate)
                f = a.halfDayStart ? 0.5 : 1;
            else if (date === a.startDate && a.halfDayStart)
                f = 0.5;
            else if (date === a.endDate && a.halfDayEnd)
                f = 0.5;
        }
        const half = !wfa && f === 0.5;
        const label = `${a.specialLeaveKind ? `${a.type} — ${a.specialLeaveKind}` : a.type}${half ? ' · half day' : ''}`;
        if (!best || f > best.fraction)
            best = { label, fraction: f };
    }
    return best;
}
export function absenceFractionForDate(date, absences) {
    let frac = 0;
    for (const a of absences) {
        if (a.status !== 'approved')
            continue;
        if (a.type === 'Work from anywhere')
            continue;
        if (date < a.startDate || date > a.endDate)
            continue;
        let f = 1;
        if (a.startDate === a.endDate)
            f = a.halfDayStart ? 0.5 : 1;
        else if (date === a.startDate && a.halfDayStart)
            f = 0.5;
        else if (date === a.endDate && a.halfDayEnd)
            f = 0.5;
        frac = Math.max(frac, f);
    }
    return Math.min(1, frac);
}
/**
 * Minutes an overtime-comp absence covering [startISO,endISO] reserves against
 * the surplus = the PER-DAY target hours of the days it covers (part-time
 * correct). Weekends / holidays / non-working weekdays contribute 0; half-day
 * boundaries are halved. A legacy full-timer resolves to weekly/5 per day.
 */
export function overtimeCompRangeMinutes(workTimeUser, holidays, startISO, endISO, halfDayStart, halfDayEnd) {
    let min = 0;
    for (const date of eachDate(startISO, endISO)) {
        if (isWeekend(date))
            continue;
        if (holidays && holidays.has(date))
            continue;
        const entry = workTimeUser ? resolveWorkTimeEntry(workTimeUser, date) : null;
        const h = entry ? dayHoursForDate(entry, date) : 0; // 0 on a non-working weekday
        if (h <= 0)
            continue;
        let frac = 1;
        if (startISO === endISO)
            frac = halfDayStart || halfDayEnd ? 0.5 : 1;
        else if (date === startISO && halfDayStart)
            frac = 0.5;
        else if (date === endISO && halfDayEnd)
            frac = 0.5;
        if (isHalfHoliday(holidays, date))
            frac = Math.min(frac, 0.5);
        min += h * 60 * frac;
    }
    return Math.round(min);
}
/** Overtime-compensation minutes reserved against the surplus = Σ over comp
 *  absences (approved + submitted) of the per-day target hours they cover. */
export function overtimeCompensationMinutes(absences, leaveTypes, workTimeUser, holidays) {
    let total = 0;
    for (const a of absences) {
        if (a.status !== 'approved' && a.status !== 'submitted')
            continue;
        if (!isOvertimeCompensationType(leaveTypes, a.type))
            continue;
        if (a.skipOvertimeDeduction)
            continue;
        total += overtimeCompRangeMinutes(workTimeUser, holidays, a.startDate, a.endDate, !!a.halfDayStart, !!a.halfDayEnd);
    }
    return total;
}
/** Per-day overtime-comp minutes split into already-taken (started on/before
 *  today, approved) vs planned (future approved + submitted) — for the team
 *  overview / offboarding displays. */
export function overtimeCompSplitMinutes(absences, leaveTypes, workTimeUser, holidays, todayISO) {
    let takenMin = 0;
    let plannedMin = 0;
    for (const a of absences) {
        if (!isOvertimeCompensationType(leaveTypes, a.type))
            continue;
        if (a.skipOvertimeDeduction)
            continue;
        if (a.status !== 'approved' && a.status !== 'submitted')
            continue;
        const m = overtimeCompRangeMinutes(workTimeUser, holidays, a.startDate, a.endDate, !!a.halfDayStart, !!a.halfDayEnd);
        if (a.status === 'approved' && a.startDate <= todayISO)
            takenMin += m;
        else
            plannedMin += m;
    }
    return { takenMin, plannedMin };
}
/** Inclusive YYYY-MM-DD iterator (UTC-based, TZ-safe). */
export function eachDate(fromStr, toStr) {
    const out = [];
    const to = new Date(`${toStr}T00:00:00Z`).getTime();
    const d = new Date(`${fromStr}T00:00:00Z`);
    while (d.getTime() <= to) {
        out.push(d.toISOString().slice(0, 10));
        d.setUTCDate(d.getUTCDate() + 1);
    }
    return out;
}
export function resolveTimeSummaryGroups(config) {
    return (config?.timeSummaryGroups ?? []).filter((g) => g && g.name && Array.isArray(g.entities));
}
export function userInTimeSummaryGroup(employmentEntity, group) {
    const e = (employmentEntity ?? '').trim().toLowerCase();
    return group.entities.some((x) => x.trim().toLowerCase() === e);
}
export const TIME_SUMMARY_PERIODS = ['Q1', 'Q2', 'Q3', 'Q4', 'FY'];
export function timeSummaryPeriod(key, year) {
    const yy = String(year % 100).padStart(2, '0');
    if (key === 'FY')
        return { key, year, label: `FY'${yy}'`, subject: `FY'${yy}' Time tracking summary`, from: `${year}-01-01`, to: `${year}-12-31` };
    const q = Number(key.slice(1));
    const lastMonth = q * 3;
    const lastDay = new Date(Date.UTC(year, lastMonth, 0)).getUTCDate();
    return {
        key, year, label: `${key}/${yy}`, subject: `${key}/${yy} time tracking summary`,
        from: `${year}-${String(lastMonth - 2).padStart(2, '0')}-01`, to: `${year}-${String(lastMonth).padStart(2, '0')}-${lastDay}`,
    };
}
/** The scheduled period for a 1st-of-month run: Jan→FY of the prior year; Apr→Q1; Jul→Q2; Oct→Q3; else null. */
export function timeSummaryPeriodForRun(now) {
    const m = now.getUTCMonth();
    if (m === 0)
        return timeSummaryPeriod('FY', now.getUTCFullYear() - 1);
    const key = m === 3 ? 'Q1' : m === 6 ? 'Q2' : m === 9 ? 'Q3' : null;
    return key ? timeSummaryPeriod(key, now.getUTCFullYear()) : null;
}
//# sourceMappingURL=timeTracking.js.map